import {useState} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {bodyRegular, headingXS} from '~/shared/theme/typography';
import {media} from '~/shared/theme/media';
import PieButton from '~/shared/theme/pie/PieButton';
import {getLocalizationService} from '~/shared/services/localisationService';
import ModalErrorView from '~/shared/components/ModalErrorView/ModalErrorView';

const Root = styled.div`
  position: relative;
  ${media.minLargeMobile`
    width: auto;
    max-width: 500px;
  `}

  &:first-child {
    overflow-y: hidden;
    background-color: ${({theme}) => theme.colors.surface};
    width: calc(100vw - 32px);
  }
`;

const Header = styled.p<{isError?: boolean}>`
  ${headingXS}
  color: ${({theme}) => theme.colors.charcoal80};
  ${({isError}) => (isError ? `
    min-height: 56px;
  ` : `
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding: 16px 0;
  `)}
  text-align: center;
`;

const Content = styled(FlexColumn)`
  padding: 32px 20px 20px;
  ${media.minLargeMobile`
    padding: 32px;
  `}
`;

const WarningText = styled.p`
  ${bodyRegular}
  color: ${({theme}) => theme.colors.contentDefault};
  margin-bottom: 32px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${media.minLargeMobile`
    flex-direction: row-reverse;
  `}
`;

const StyledPieButton = styled(PieButton)`
  width: 100%;
`;

type RemoveCreditCardModalProps = {
  closeModal: () => void;
  args: {
    id: number;
    cardName: string;
    type: string;
    removePaymentMethodHandler?: (args: {id: number; type: string}) => Promise<boolean>;
  };
};

export const RemoveCreditCardModal = ({closeModal, args}: RemoveCreditCardModalProps) => {
  const {t} = getLocalizationService();
  const [isError, setIsError] = useState(false);
  const [isRemovePaymentMethodRequestLoading, setIsRemovePaymentMethodRequestLoading] = useState(false);
  const {cardName, removePaymentMethodHandler, id, type} = args;

  const removePaymentClickHandler = async () => {
    setIsRemovePaymentMethodRequestLoading(true);
    if (removePaymentMethodHandler) {
      const result = await removePaymentMethodHandler({id, type});
      if (result) {
        closeModal();
      }
      if (!result) {
        setIsError(true);
      }
    }
    setIsRemovePaymentMethodRequestLoading(false);
  };

  return (
    <Root>
      <Header id="modal-title" isError={isError}>
        {!isError && t('payment_method_remove_title')}
      </Header>
      {isError ? (
        <ModalErrorView
          title={t('payment_method_was_not_removed_title')}
          body={t('payment_method_was_not_removed_body')}
          onClose={closeModal}
        />
      ) : (
        <Content>
          <WarningText>{t('payment_method_remove_validation_message', {paymentMethodName: cardName})}</WarningText>
          <ButtonsContainer>
            <StyledPieButton
              variant="primaryXL"
              onClick={removePaymentClickHandler}
              disabled={isRemovePaymentMethodRequestLoading}
              isLoading={isRemovePaymentMethodRequestLoading}
            >
              {t('remove')}
            </StyledPieButton>
            <StyledPieButton variant="secondaryXL" onClick={closeModal}>{t('cancel')}</StyledPieButton>
          </ButtonsContainer>
        </Content>
      )}
    </Root>
  );
};

attachModal('removeCreditCardModal', RemoveCreditCardModal);
